// src/context/CryptoContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { getKeyring, saveKeyring } from "../firebase/keyringService";
import {
  createKeyring,
  unlockWithPassphrase,
  resetPassphraseWithRecovery,
} from "../utils/crypto";
import { changePassphrase } from "../utils/crypto";

const CryptoContext = createContext(null);

export const CryptoProvider = ({ user, children }) => {
  const uid = user?.uid || null;
  const [status, setStatus] = useState("signed-out");
  const [keyring, setKeyring] = useState(null);
  const [dek, setDek] = useState(null);
  const pending = useRef(null); // setup done, waiting user confirm recovery key saved

  // Load keyring when user changes. Sign out → key wiped.
  useEffect(() => {
    let cancelled = false;
    setDek(null);
    setKeyring(null);
    pending.current = null;

    if (!uid) {
      setStatus("signed-out");
      return;
    }
    setStatus("loading");
    getKeyring(uid)
      .then((kr) => {
        if (cancelled) return;
        setKeyring(kr);
        setStatus(kr ? "locked" : "needs-setup");
      })
      .catch((e) => {
        console.error("Keyring load failed", e);
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const value = useMemo(
    () => ({
      status,
      dek,

      /** First setup. Returns recoveryKey to show. Not unlocked until completeSetup(). */
      setup: async (passphrase) => {
        const existing = await getKeyring(uid); // guard: never overwrite keyring
        if (existing) throw new Error("KEYRING_EXISTS");
        const res = await createKeyring(passphrase);
        await saveKeyring(uid, res.keyring);
        pending.current = res;
        return res.recoveryKey;
      },

      /** Call after user confirm recovery key saved. */
      completeSetup: () => {
        const p = pending.current;
        if (!p) return;
        setKeyring(p.keyring);
        setDek(p.dek);
        setStatus("unlocked");
        pending.current = null;
      },

      unlock: async (passphrase) => {
        const k = await unlockWithPassphrase(keyring, passphrase); // throws if wrong
        setDek(k);
        setStatus("unlocked");
      },

      /** Forgot passphrase. Recovery key + new passphrase. */
      recover: async (recoveryKey, newPassphrase) => {
        const newKeyring = await resetPassphraseWithRecovery(
          keyring,
          recoveryKey,
          newPassphrase,
        );
        await saveKeyring(uid, newKeyring);
        const k = await unlockWithPassphrase(newKeyring, newPassphrase);
        setKeyring(newKeyring);
        setDek(k);
        setStatus("unlocked");
      },
      changePass: async (oldPass, newPass) => {
        const newKeyring = await changePassphrase(keyring, oldPass, newPass); // throws if old wrong
        await saveKeyring(uid, newKeyring);
        setKeyring(newKeyring);
      },
      lock: () => {
        setDek(null);
        setStatus(keyring ? "locked" : "needs-setup");
      },
    }),
    [status, dek, keyring, uid],
  );

  return (
    <CryptoContext.Provider value={value}>{children}</CryptoContext.Provider>
  );
};

export const useCrypto = () => {
  const ctx = useContext(CryptoContext);
  if (!ctx) throw new Error("useCrypto must be inside CryptoProvider");
  return ctx;
};
